import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import "../../../styles/pages/ouber.scss";
import { Tag, Space, Typography, Input, Select, Row, Col, DatePicker, Button, Tooltip, Tour } from "antd";
import { EyeOutlined, EditOutlined, QuestionCircleOutlined, SearchOutlined, SyncOutlined, WarningOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import PvTable from "../../../components/Table/PvTable";
import DataView from "../../../components/DataView/DataView";
import PvCardList from "../../../components/DataView/PvCardList";
import AdminViewTripRequestModal from "../../../components/Ouber/modals/admin/AdminViewTripRequestModal";
import AdminLinkTripModal from "../../../components/Ouber/modals/admin/AdminLinkTripModal";
import AdminEditRequestModal from "../../../components/Ouber/modals/admin/AdminEditRequestModal";
import { showErrorToast, showSuccessToast } from "../../../components/Ouber/ToastNotification";
import OuberPageInfoIcon from "../../../components/Ouber/OuberPageInfoIcon";
import { useOuberService } from "../../../services";
import { formatStatusWithActor } from "../../../utils/ouberUtils";

// Companions are optional on submission, but a request with fewer than this
// many total passengers is flagged here for the Admin's attention — they
// decide whether to add more passengers or decline it.
const MIN_RECOMMENDED_PASSENGERS = 3;

// Deterministic color per request_group_id so rows submitted together in
// the same "Request Trip" (companions) get a matching left-border + tint,
// regardless of sort/filter order.
const GROUP_COLORS = ["#1677ff", "#52c41a", "#faad14", "#eb2f96", "#722ed1", "#13c2c2", "#fa541c", "#2f54eb"];
const getGroupColor = (groupId) => {
  if (!groupId) return null;
  const str = String(groupId);
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
  }
  return GROUP_COLORS[hash % GROUP_COLORS.length];
};

/**
 * The purpose of this page is to allow administrators to review,
 * approve, or decline individual trip requests from employees.
 */
const AdminManageTripReqPage = () => {
  const { dataView } = useSelector((state) => state.generalSlice);
  const { details: profile, account_types = [] } = useSelector((state) => state.accountDetailSlice);

  const isSuperAdmin = profile?.account_type === "Super Admin" || account_types.includes("superadmin") || account_types.includes("xadmin");

  const [selectedRequest, setSelectedRequest] = useState(null);
  const [isViewModalVisible, setIsViewModalVisible] = useState(false);
  const [isLinkModalVisible, setIsLinkModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);

  const [filterName, setFilterName] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [filterStatus, setFilterStatus] = useState(null);
  const [filterDate, setFilterDate] = useState(null);
  const [isTourOpen, setIsTourOpen] = useState(false);
  const filtersRef = useRef(null);
  const viewControlsRef = useRef(null);
  const tableRef = useRef(null);

  const { service: ouberService, loading } = useOuberService();
  const [data, setData] = useState([]);

  const tourSeenStorageKey = profile?.email
    ? `ouber-trip-requests-tour-seen:${profile.email}`
    : null;

  useEffect(() => {
    if (!tourSeenStorageKey) return;

    try {
      if (!window.localStorage.getItem(tourSeenStorageKey)) {
        setIsTourOpen(true);
      }
    } catch {
      // localStorage can be unavailable (private browsing, etc.) — the tour
      // simply won't auto-open in that case, no need to fail loudly.
    }
  }, [tourSeenStorageKey]);

  const handleTourClose = () => {
    setIsTourOpen(false);

    if (!tourSeenStorageKey) return;

    try {
      window.localStorage.setItem(tourSeenStorageKey, "1");
    } catch {
      // Ignore — worst case the tour auto-opens again next visit.
    }
  };

  const tourSteps = useMemo(
    () => [
      {
        title: "Search and filter",
        description:
          "Narrow requests down by requestor name, pickup/destination, date, or status.",
        target: () => filtersRef.current,
      },
      {
        title: "Table or card view",
        description: "Switch layouts, or refresh the list to pull the latest data.",
        target: () => viewControlsRef.current,
      },
      {
        title: "Review a request",
        description:
          "Click the eye icon to view a request's full details — from there you can approve, decline, or link it to a trip. The edit icon lets you update a request you're still able to act on.",
        target: () => tableRef.current,
      },
    ],
    [],
  );

  // Fetches trip requests from the service with filters for name, location, status, and date
  const fetchRequests = useCallback(async () => {
    try {
      const params = { 
        search_name: filterName || undefined,
        search_location: filterLocation || undefined,
        status: filterStatus || undefined,
        trip_date: filterDate ? filterDate.format("YYYY-MM-DD") : undefined,
        scope: "unit"
      };
      
      const response = await ouberService.getTripRequests(params);
      const requestsListRaw = Array.isArray(response) ? response : (response?.data || []);
      const requestsList = requestsListRaw.filter(r => r !== null && r !== undefined);

      const reqs = requestsList.map(r => ({ 
        ...r, 
        key: (r.trip_request_id || Math.random()).toString(), 
        id: (r.trip_request_id || "").toString(), 
        trip_date: r.trip_date ? r.trip_date.split("T")[0] : null 
      }));
      
      // Formats raw backend request data into a structure suitable for UI display
      setData(reqs);
    } catch (err) {
      showErrorToast("Fetch Error", "Failed to load trip requests.");
    }
  }, [filterName, filterLocation, filterStatus, filterDate, ouberService]);

  const fetchRequestsRef = useRef(fetchRequests);
  useEffect(() => {
    fetchRequestsRef.current = fetchRequests;
  }, [fetchRequests]);

  // Executes a debounced fetch whenever any of the filter values are updated by the admin
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchRequestsRef.current();
    }, 500);
    return () => clearTimeout(handler);
  }, [filterName, filterLocation, filterStatus, filterDate]);

  // Show one row per group submission (the leader — where trip_request_id ===
  // request_group_id) instead of a separate row per passenger; the "Linked
  // Passengers" column and the View modal already surface everyone else in
  // the group. Solo (non-grouped) requests pass through unchanged.
  const filteredData = useMemo(
    () => data.filter(r => !r.request_group_id || r.trip_request_id === r.request_group_id),
    [data]
  );

  const columns = [
    { title: "Request ID", dataIndex: "id", key: "id", align: "center" },
    {
      title: "Request Status",
      dataIndex: "request_status",
      key: "request_status",
      align: "center",
      render: (status, record) => {
        let tagClass = "tag-pending";

        if (status === "Requested to Join") {
          tagClass = "tag-pending";
        } else if (status === "Approved by AO" || status === "Created by AO") {
          tagClass = "tag-approved";
        } else if (status === "Completed Trip") {
          tagClass = "tag-completed";
        } else if (status === "Declined by AO" || status === "Cancelled by AO" || status === "Cancelled by User") {
          tagClass = "tag-cancelled";
        }

        return (
          <Tag
            className={tagClass}
            style={{
              borderRadius: "12px",
              minWidth: "80px",
              textAlign: "center",
            }}
          >
            {formatStatusWithActor(status, record.status_changed_by)}
          </Tag>
        );
      },
    },
    { 
      title: "Requestor/Head Name",
      dataIndex: "employee",
      key: "employee", 
      align: "center",
      render: (emp, record) => record.external_passenger_name || (emp ? `${emp.first_name} ${emp.last_name}` : "N/A")
    },
    {
      title: "Unit",
      dataIndex: "unit",
      key: "unit",
      align: "center",
      render: (unit) => unit || "N/A"
    },
    {
      title: "Linked Passengers",
      key: "group",
      align: "center",
      render: (_, record) => {
        const ACTIVE_STATUSES = ["Approved by AO", "Created by AO"];
        const others = (record.group_members || []).filter(m => m.trip_request_id !== record.trip_request_id);
        // Only passengers the AO has actually approved count toward the
        // total — a declined or still-pending passenger never occupied a
        // real seat, so they shouldn't inflate the headcount.
        const approvedOthers = others.filter(m => ACTIVE_STATUSES.includes(m.request_status));
        const names = approvedOthers.map(m => m.employee
          ? `${m.employee.first_name || ""} ${m.employee.last_name || ""}`.trim()
          : (m.external_passenger_name || "Guest"));
        const passengerCount = approvedOthers.length + (ACTIVE_STATUSES.includes(record.request_status) ? 1 : 0);
        const pendingCount = [record, ...others].filter(m => m.request_status === "Requested to Join").length;
        const pendingBadge = pendingCount > 0 && (
          <Tooltip title={`${pendingCount} still awaiting approval`}>
            <Tag color="gold" style={{ margin: 0, fontSize: "11px", lineHeight: "16px", padding: "0 6px" }}>
              +{pendingCount} pending
            </Tag>
          </Tooltip>
        );

        // Trips the AO created directly (this row is the Head of the
        // Party) skip the "likely to be declined" warning — the Admin
        // already decided who's on it, there's no separate approval step
        // to flag.
        const isCreatedByAO = record.request_status === "Created by AO";

        if (isCreatedByAO) {
          const color = getGroupColor(record.request_group_id);
          return (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
              <Tooltip title={approvedOthers.length > 0 ? `Passengers: ${names.join(", ")}` : "No other passengers"}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", cursor: "default" }}>
                  <span style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: color, display: "inline-block" }} />
                  <span style={{ fontSize: "12px" }}>{passengerCount} passenger{passengerCount === 1 ? "" : "s"}</span>
                </span>
              </Tooltip>
              {pendingBadge}
            </span>
          );
        }

        if (passengerCount === 0) {
          return (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
              <span style={{ color: "#bfbfbf", fontSize: "12px" }}>—</span>
              {pendingBadge}
            </span>
          );
        }

        if (passengerCount < MIN_RECOMMENDED_PASSENGERS) {
          const tooltipMsg = approvedOthers.length > 0
            ? `Only ${passengerCount} passengers (with ${names.join(", ")}) — below the recommended minimum of ${MIN_RECOMMENDED_PASSENGERS}. Add more passengers or decline.`
            : `Only ${passengerCount} passenger${passengerCount === 1 ? "" : "s"} — below the recommended minimum of ${MIN_RECOMMENDED_PASSENGERS}. Add more passengers or decline.`;
          return (
            <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
              <Tooltip title={tooltipMsg}>
                <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", cursor: "default", color: "#d46b08" }}>
                  <WarningOutlined />
                  <span style={{ fontSize: "12px" }}>{passengerCount} passenger{passengerCount === 1 ? "" : "s"}</span>
                </span>
              </Tooltip>
              {pendingBadge}
            </span>
          );
        }

        const color = getGroupColor(record.request_group_id);
        return (
          <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
            <Tooltip title={`Submitted together with: ${names.join(", ")}`}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: "6px", cursor: "default" }}>
                <span style={{ width: "10px", height: "10px", borderRadius: "50%", backgroundColor: color, display: "inline-block" }} />
                <span style={{ fontSize: "12px" }}>{passengerCount} passengers</span>
              </span>
            </Tooltip>
            {pendingBadge}
          </span>
        );
      },
    },

    { title: "Trip Date", dataIndex: "trip_date", key: "trip_date", align: "center" },
    {
      title: "Pickup Venue",
      dataIndex: "pickup_venue",
      key: "pickup_venue",
      align: "center",
    },
    { 
      title: "Destination", 
      dataIndex: "dropoff_location", 
      key: "dropoff_location", 
      align: "center",
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <Space size="middle" style={{ display: 'flex', justifyContent: 'center' }}>
          <EyeOutlined 
            style={{ fontSize: "18px", cursor: "pointer", color: "#1890ff" }} 
            onClick={() => {
              setSelectedRequest(record);
              setIsViewModalVisible(true);
            }} 
          />
          {record.trip_id && record.request_status !== "Cancelled by User" && (isSuperAdmin || (!["Completed Trip", "Declined by AO", "Cancelled by AO"].includes(record.request_status) &&
           record.trip && record.trip.created_by_employee_id === profile?.id)) && (
            <EditOutlined 
              style={{ fontSize: "18px", cursor: "pointer", color: "#52c41a" }} 
              onClick={() => {
                setSelectedRequest(record);
                setIsEditModalVisible(true);
              }} 
            />
          )}
        </Space>
      ),
    },
    { title: "Remarks", dataIndex: "remarks", key: "remarks", align: "center" },
  ];

  return (
    <div className="ouber-page-container">
      <Space className="ouber-page-header">
        <Typography.Title level={3} className="ouber-page-title">
          Manage Trip Requests
          <OuberPageInfoIcon
            message="Manage Trip Requests"
            description="Approve, decline, and edit trip requests here. Requests are based on the trips you've made or requests initiated by employees in your unit."
          />
        </Typography.Title>
        <Tooltip title="Take a tour of this page">
          <Button
            icon={<QuestionCircleOutlined />}
            onClick={() => setIsTourOpen(true)}
          />
        </Tooltip>
      </Space>

      <div className="ouber-filter-container" ref={filtersRef}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Input 
              placeholder="Search Requestor/Head Name" 
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              value={filterName}
              onChange={(e) => setFilterName(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Input 
              placeholder="Search Pickup or Destination" 
              prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
              value={filterLocation}
              onChange={(e) => setFilterLocation(e.target.value)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <DatePicker 
              placeholder="Filter by Date"
              style={{ width: "100%" }}
              format="MM-DD-YYYY"
              value={filterDate}
              onChange={(date) => setFilterDate(date)}
              allowClear
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Select
              placeholder="Filter by Status"
              style={{ width: "100%" }}
              allowClear
              value={filterStatus}
              onChange={(val) => setFilterStatus(val)}
              options={[
                { value: "Completed Trip", label: "Completed Trip" },
                { value: "Approved by AO", label: "Approved by AO" },
                { value: "Requested to Join", label: "Requested to Join" },
                { value: "Declined by AO", label: "Declined by AO" },
                { value: "Cancelled by AO", label: "Cancelled by AO" },
                { value: "Cancelled by User", label: "Cancelled by User" }
              ]}
            />
          </Col>
        </Row>
      </div>

      <div ref={viewControlsRef} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: "12px" }}>
        <DataView />
        <Button
          icon={<SyncOutlined />}
          onClick={fetchRequests}
          loading={loading}
          className="ouber-btn-primary"
          style={{ marginBottom: 0 }}
        >
          Refresh
        </Button>
      </div>

      <div ref={tableRef}>
      {dataView === "table" ? (
        <PvTable
          data={filteredData}
          columns={columns}
          loading={loading}
          onRowClick={() => {}}
          rowStyle={(record) => {
            const color = getGroupColor(record.request_group_id);
            return color ? { borderLeft: `4px solid ${color}`, backgroundColor: `${color}0d` } : undefined;
          }}
        />
      ) : (
        <PvCardList
          data={filteredData}
          columns={columns}
          onRowClick={() => {}}
          loading={loading}
          maxActiveRow={filteredData.length}
        />
      )}
      </div>

      <AdminViewTripRequestModal
        isOpen={isViewModalVisible}
        onClose={() => setIsViewModalVisible(false)}
        requestData={selectedRequest}
        onEdit={(req) => {
          setIsViewModalVisible(false);
          setIsEditModalVisible(true);
        }}
        onCreateLinkedTrip={(req) => {
          setIsViewModalVisible(false);
          setIsLinkModalVisible(true);
        }}
        onDecline={async (req) => {
          try {
            await ouberService.updateRequestStatus(req.id, { request_status: "Declined by AO" });
            showSuccessToast("Success", "Trip request has been declined.");
            setIsViewModalVisible(false);
            fetchRequests();
          } catch (e) {
            const errorMsg = e.response?.data?.message || "Could not decline request.";
            showErrorToast("Error", errorMsg);
          }
        }}
        onUpdatePassengerStatus={async (tripRequestId, status) => {
          try {
            await ouberService.updateRequestStatus(tripRequestId, { request_status: status });
            showSuccessToast("Success", status === "Approved by AO" ? "Passenger approved." : "Passenger declined.");
            setIsViewModalVisible(false);
            fetchRequests();
          } catch (e) {
            const errorMsg = e.response?.data?.message || "Could not update passenger status.";
            showErrorToast("Error", errorMsg);
          }
        }}
      />

      <AdminLinkTripModal
        open={isLinkModalVisible}
        onCancel={() => setIsLinkModalVisible(false)}
        requestData={selectedRequest}
        onSave={async (newTrip) => {
          try {
            await ouberService.createTrip(newTrip);
            showSuccessToast("Success", "Trip successfully created for this request.");
            setIsLinkModalVisible(false);
            fetchRequests();
          } catch (e) {
            const errorMsg = e.response?.data?.message || "Could not process linked trip.";
            showErrorToast("Error", errorMsg);
            throw e;
          }
        }}
      />

      <AdminEditRequestModal
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        passengerData={selectedRequest}
        onSave={async (updatedData) => {
          try {
            // Assuming this endpoint allows partial updates on the request
            await ouberService.updateRequestStatus(selectedRequest.id, updatedData);
            showSuccessToast("Success", "Request details successfully updated.");
            setIsEditModalVisible(false);
            fetchRequests();
          } catch (e) {
            const errorMsg = e.response?.data?.message || "Could not update request details.";
            showErrorToast("Error", errorMsg);
          }
        }}
      />

      <Tour open={isTourOpen} onClose={handleTourClose} steps={tourSteps} />
    </div>
  );
};

export default AdminManageTripReqPage;
